import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Xmark } from "iconoir-react";
import { useLanguage } from "@/components/LanguageContext";

export function Navbar() {
  const { language, setLanguage, t } = useLanguage();

  const navLinks = [
    { label: t("Accueil", "Home"), href: "/" },
    { label: t("À Propos", "About"), href: "/about" },
    { label: t("Services", "Services"), href: "/services" },
    { label: t("Solutions", "Solutions"), href: "/solutions" },
    { label: t("Projets", "Projects"), href: "/projects" },
    { label: t("Logiciels", "Software"), href: "/software" },
    { label: t("Partenaires", "Partners"), href: "/partners" },
    { label: t("Contact", "Contact"), href: "/contact" },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-warm-white/90 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group mt-1">
           <div className={`relative transition-all duration-300 ${scrolled ? 'w-[180px] h-10' : 'w-[220px] h-12'}`}>
             <img 
               src={scrolled ? "/images/logo-dark.png" : "/images/logo-light.png"} 
               alt={t("Logo AVISIUM Solutions Group", "AVISIUM Solutions Group Logo")} 
               className="w-full h-full object-contain object-left pointer-events-none"
             />
           </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm transition-colors duration-300 relative group font-medium ${
                scrolled ? "text-graphite hover:text-charcoal" : "text-warm-white/80 hover:text-warm-white drop-shadow-md"
              }`}
            >
              {link.label}
              <span className={`absolute -bottom-1 left-0 w-0 h-[1.5px] transition-all duration-300 group-hover:w-full ${scrolled ? 'bg-amber' : 'bg-warm-white'}`} />
            </Link>
          ))}
          
          <div className="flex items-center ml-2 border-l border-mineral-dark/30 pl-4 space-x-2">
            <button 
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              className={`text-xs font-bold uppercase tracking-wider transition-colors px-2 py-1 rounded-md ${scrolled ? "text-graphite hover:text-charcoal bg-mineral/20" : "text-warm-white hover:text-amber bg-white/10"}`}
            >
              {language === 'fr' ? 'FR' : 'EN'}
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center px-5 py-2 text-sm font-medium text-warm-white bg-charcoal rounded-full hover:bg-amber-dark transition-colors duration-300"
            >
              {t("Nous Contacter", "Get in Touch")}
            </Link>
          </div>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden p-2 transition-colors duration-300 ${scrolled ? 'text-charcoal' : 'text-warm-white'}`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <Xmark width={24} height={24} className="text-charcoal" /> : <Menu width={24} height={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-warm-white/95 backdrop-blur-md border-t border-mineral-dark shadow-xl"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block text-base font-medium text-graphite hover:text-charcoal transition-colors focus:outline-none"
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 mt-2 border-t border-mineral-dark flex flex-col gap-4">
                <button 
                  onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                  className="inline-flex justify-center items-center py-2 text-sm font-bold uppercase tracking-wider text-charcoal bg-mineral rounded-md"
                >
                  {language === 'fr' ? 'Passer en English' : 'Switch to Français'}
                </button>
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex w-full justify-center items-center px-6 py-3 text-sm font-medium text-warm-white bg-charcoal hover:bg-amber-dark transition-colors duration-300 rounded-[20px]"
                >
                  {t("Nous Contacter", "Get in Touch")}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
